import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { productService } from '../services/productService';
import api from '../services/api';
import { getImageUrl } from '../utils/imageHelper';
import Loading from '../components/common/Loading';
import { DefaultEditor, EditorProvider } from 'react-simple-wysiwyg';
import './CreateProduct.css';

const EditProduct = () => {
  const { productId } = useParams();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [categories, setCategories] = useState([]);
  const [categoriesLoading, setCategoriesLoading] = useState(true);
  const [error, setError] = useState('');
  const [primaryImage, setPrimaryImage] = useState(null);
  const [primaryImagePreview, setPrimaryImagePreview] = useState(null);
  const [existingPrimaryImage, setExistingPrimaryImage] = useState(null);
  const [additionalImages, setAdditionalImages] = useState([]);
  const [additionalImagesPreview, setAdditionalImagesPreview] = useState([]);
  const [existingImages, setExistingImages] = useState([]);
  const [colors, setColors] = useState([]);
  const [highlights, setHighlights] = useState([]);
  const [formData, setFormData] = useState({
    name: '',
    description: '',
    price: '',
    discounted_price: '',
    category_id: '',
    sku: '',
    stock_quantity: 0,
    is_best_seller: false,
    is_new_arrival: false,
    status: 'active',
  });

  const loadProduct = useCallback(async () => {
    try {
      setLoading(true);
      setError('');
      const product = await productService.getProductById(productId);

      // Set form data
      setFormData({
        name: product.name || '',
        description: product.description || '',
        price: product.price || '',
        discounted_price: product.discounted_price || '',
        category_id: product.category_id || '',
        sku: product.sku || '',
        stock_quantity: product.stock_quantity || 0,
        is_best_seller: product.is_best_seller || false,
        is_new_arrival: product.is_new_arrival || false,
        status: product.status || 'active',
      });

      // Load existing colors
      if (Array.isArray(product.colors)) {
        const sorted = [...product.colors].sort(
          (a, b) => (a.display_order || 0) - (b.display_order || 0)
        );
        setColors(
          sorted.map((c) => ({ name: c.name, hex_code: c.hex_code }))
        );
      } else {
        setColors([]);
      }

      // Load existing highlights
      if (Array.isArray(product.highlights)) {
        const sortedHighlights = [...product.highlights].sort(
          (a, b) => (a.display_order || 0) - (b.display_order || 0)
        );
        setHighlights(
          sortedHighlights.map((h) => ({
            label: h.label || '',
            value: h.value || '',
          }))
        );
      } else {
        setHighlights([]);
      }

      // Set existing images
      if (product.images && product.images.length > 0) {
        const primary = product.images.find((img) => img.is_primary) || product.images[0];
        const additional = product.images.filter((img) => !img.is_primary);

        setExistingPrimaryImage(primary);
        setExistingImages(additional);

        // Set previews for existing images
        if (primary) {
          setPrimaryImagePreview(getImageUrl(primary.image_url));
        }
        setAdditionalImagesPreview(
          additional.map((img) => getImageUrl(img.image_url))
        );
      }
    } catch (err) {
      let errorMessage = 'Failed to load product';
      if (err.message) {
        errorMessage = err.message;
      } else if (err.response?.data?.message) {
        errorMessage = err.response.data.message;
      }
      setError(errorMessage);
    } finally {
      setLoading(false);
    }
  }, [productId]);

  useEffect(() => {
    loadProduct();
    loadCategories();
  }, [loadProduct]);

  const loadCategories = async () => {
    try {
      setCategoriesLoading(true);
      const response = await api.get('/products/categories');
      setCategories(response.data.data?.categories || []);
    } catch (err) {
      console.error('Failed to load categories:', err);
    } finally {
      setCategoriesLoading(false);
    }
  };

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value,
    }));
  };

  const handlePrimaryImageChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      setPrimaryImage(file);
      setExistingPrimaryImage(null); // Clear existing image when new one is selected
      const reader = new FileReader();
      reader.onloadend = () => {
        setPrimaryImagePreview(reader.result);
      };
      reader.readAsDataURL(file);
    }
  };

  const handleAdditionalImagesChange = (e) => {
    const files = Array.from(e.target.files);
    if (files.length > 0) {
      setAdditionalImages((prev) => [...prev, ...files]);
      files.forEach((file) => {
        const reader = new FileReader();
        reader.onloadend = () => {
          setAdditionalImagesPreview((prev) => [...prev, reader.result]);
        };
        reader.readAsDataURL(file);
      });
    }
  };

  const removePrimaryImage = () => {
    setPrimaryImage(null);
    setPrimaryImagePreview(null);
    setExistingPrimaryImage(null);
  };

  const addColor = () => {
    setColors((prev) => [...prev, { name: '', hex_code: '#000000' }]);
  };

  const updateColor = (index, field, value) => {
    setColors((prev) =>
      prev.map((c, i) => (i === index ? { ...c, [field]: value } : c))
    );
  };

  const removeColor = (index) => {
    setColors((prev) => prev.filter((_, i) => i !== index));
  };

  const addHighlight = () => {
    setHighlights((prev) => [...prev, { label: '', value: '' }]);
  };

  const updateHighlight = (index, field, value) => {
    setHighlights((prev) =>
      prev.map((h, i) => (i === index ? { ...h, [field]: value } : h))
    );
  };

  const removeHighlight = (index) => {
    setHighlights((prev) => prev.filter((_, i) => i !== index));
  };

  const removeAdditionalImage = (index, isExisting = false) => {
    if (isExisting) {
      setExistingImages((prev) => prev.filter((_, i) => i !== index));
      setAdditionalImagesPreview((prev) => prev.filter((_, i) => i !== index));
    } else {
      const newIndex = index - existingImages.length;
      setAdditionalImages((prev) => prev.filter((_, i) => i !== newIndex));
      setAdditionalImagesPreview((prev) => {
        return prev.filter((_, i) => i !== index);
      });
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setSaving(true);

    try {
      // Validate required fields
      if (!formData.name.trim()) {
        throw new Error('Product name is required');
      }
      if (!formData.price || parseFloat(formData.price) <= 0) {
        throw new Error('Valid price is required');
      }
      if (!formData.sku.trim()) {
        throw new Error('SKU is required');
      }

      const cleanedColors = colors
        .map((c) => ({
          name: (c.name || '').trim(),
          hex_code: (c.hex_code || '').trim(),
        }))
        .filter((c) => c.name || c.hex_code);
      for (let i = 0; i < cleanedColors.length; i++) {
        if (!cleanedColors[i].name) {
          throw new Error(`Color #${i + 1}: name is required`);
        }
        if (!/^#[0-9A-Fa-f]{6}$/.test(cleanedColors[i].hex_code)) {
          throw new Error(`Color #${i + 1}: pick a valid color`);
        }
      }

      // Blank rows are dropped rather than rejected, so an empty row left over
      // from "+ Add highlight" does not block the save. display_order follows
      // the on-screen order. Always sent, so clearing every row clears the set.
      const cleanedHighlights = highlights
        .map((h) => ({
          label: (h.label || '').trim(),
          value: (h.value || '').trim(),
        }))
        .filter((h) => h.label || h.value)
        .map((h, i) => ({
          label: h.label || null,
          value: h.value,
          display_order: i,
        }));
      for (let i = 0; i < cleanedHighlights.length; i++) {
        if (!cleanedHighlights[i].value) {
          throw new Error(`Highlight #${i + 1}: value is required`);
        }
      }

      // Create FormData for multipart/form-data (if images are being updated)
      const hasNewImages = primaryImage || additionalImages.length > 0;
      let updateData;

      if (hasNewImages) {
        // Use FormData if images are being updated
        updateData = new FormData();
        updateData.append('name', formData.name);
        updateData.append('description', formData.description || '');
        updateData.append('price', formData.price);
        if (formData.discounted_price && formData.discounted_price.trim() !== '') {
          updateData.append('discounted_price', formData.discounted_price);
        } else {
          updateData.append('discounted_price', '');
        }
        updateData.append('sku', formData.sku);
        updateData.append('stock_quantity', formData.stock_quantity || 0);
        updateData.append('is_best_seller', formData.is_best_seller);
        updateData.append('is_new_arrival', formData.is_new_arrival);
        updateData.append('status', formData.status);

        if (formData.category_id) {
          updateData.append('category_id', formData.category_id);
        }

        // Add new primary image if provided
        if (primaryImage) {
          updateData.append('primaryImage', primaryImage);
        }

        // Add new additional images
        additionalImages.forEach((image) => {
          updateData.append('images', image);
        });

        updateData.append('colors', JSON.stringify(cleanedColors));
        updateData.append('highlights', JSON.stringify(cleanedHighlights));
      } else {
        // Use JSON if no images are being updated
        updateData = {
          name: formData.name,
          description: formData.description || '',
          price: formData.price,
          discounted_price: formData.discounted_price && formData.discounted_price.trim() !== '' ? formData.discounted_price : null,
          sku: formData.sku,
          stock_quantity: formData.stock_quantity || 0,
          is_best_seller: formData.is_best_seller,
          is_new_arrival: formData.is_new_arrival,
          status: formData.status,
          category_id: formData.category_id || null,
          colors: cleanedColors,
          highlights: cleanedHighlights,
        };
      }

      await productService.updateProduct(productId, updateData);
      navigate('/products', { state: { message: 'Product updated successfully!' } });
    } catch (err) {
      let errorMessage = 'Failed to update product';
      if (err.message) {
        errorMessage = err.message;
      } else if (err.response?.data?.message) {
        errorMessage = err.response.data.message;
      }
      setError(errorMessage);
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return <Loading message="Loading product..." />;
  }

  return (
    <div className="create-product-container">
      <div className="page-header">
        <h1 className="page-title">Edit Product</h1>
        <button
          onClick={() => navigate('/products')}
          className="btn-secondary"
        >
          Cancel
        </button>
      </div>

      <div className="form-container">
        <form onSubmit={handleSubmit} className="product-form">
          <div className="form-group">
            <label htmlFor="name">
              Product Name <span className="required">*</span>
            </label>
            <input
              type="text"
              id="name"
              name="name"
              value={formData.name}
              onChange={handleChange}
              required
              placeholder="Enter product name"
            />
          </div>

          <div className="form-group">
            <label htmlFor="description">Description</label>
            <div className="editor-container">
              <EditorProvider>
                <DefaultEditor
                  id="description"
                  name="description"
                  value={formData.description}
                  onChange={(e) => {
                    setFormData((prev) => ({
                      ...prev,
                      description: e.target.value,
                    }));
                  }}
                  containerProps={{ style: { height: '300px', backgroundColor: '#fff' } }}
                />
              </EditorProvider>
            </div>
          </div>

          <div className="form-row">
            <div className="form-group">
              <label htmlFor="price">
                Price <span className="required">*</span>
              </label>
              <input
                type="number"
                id="price"
                name="price"
                value={formData.price}
                onChange={handleChange}
                required
                min="0"
                step="0.01"
                placeholder="0.00"
              />
            </div>

            <div className="form-group">
              <label htmlFor="discounted_price">
                Discounted Price (Optional)
              </label>
              <input
                type="number"
                id="discounted_price"
                name="discounted_price"
                value={formData.discounted_price}
                onChange={handleChange}
                min="0"
                step="0.01"
                placeholder="0.00"
              />
            </div>
          </div>

          <div className="form-row">
            <div className="form-group">
              <label htmlFor="sku">
                SKU <span className="required">*</span>
              </label>
              <input
                type="text"
                id="sku"
                name="sku"
                value={formData.sku}
                onChange={handleChange}
                required
                placeholder="e.g., PROD-001"
              />
            </div>
          </div>

          <div className="form-row">
            <div className="form-group">
              <label htmlFor="stock_quantity">Stock Quantity</label>
              <input
                type="number"
                id="stock_quantity"
                name="stock_quantity"
                value={formData.stock_quantity}
                onChange={handleChange}
                min="0"
                placeholder="0"
              />
            </div>

            <div className="form-group">
              <label htmlFor="category_id">Category</label>
              {categoriesLoading ? (
                <select disabled>
                  <option>Loading categories...</option>
                </select>
              ) : (
                <select
                  id="category_id"
                  name="category_id"
                  value={formData.category_id}
                  onChange={handleChange}
                >
                  <option value="">Select a category (optional)</option>
                  {categories.map((category) => (
                    <option key={category.id} value={category.id}>
                      {category.name}
                    </option>
                  ))}
                </select>
              )}
            </div>
          </div>

          <div className="form-row">
            <div className="form-group">
              <label htmlFor="status">Status</label>
              <select
                id="status"
                name="status"
                value={formData.status}
                onChange={handleChange}
              >
                <option value="active">Active</option>
                <option value="inactive">Inactive</option>
                <option value="draft">Draft</option>
              </select>
            </div>
          </div>

          <div className="form-group">
            <label>Colors (Optional)</label>
            <div className="colors-list">
              {colors.map((color, index) => (
                <div key={index} className="color-row">
                  <input
                    type="color"
                    value={color.hex_code}
                    onChange={(e) =>
                      updateColor(index, 'hex_code', e.target.value)
                    }
                    className="color-swatch-input"
                    aria-label={`Color ${index + 1} hex`}
                  />
                  <input
                    type="text"
                    value={color.name}
                    onChange={(e) => updateColor(index, 'name', e.target.value)}
                    placeholder="Color name (e.g., Midnight Black)"
                    className="color-name-input"
                  />
                  <button
                    type="button"
                    onClick={() => removeColor(index)}
                    className="btn-remove-color"
                    aria-label="Remove color"
                  >
                    ×
                  </button>
                </div>
              ))}
              <button
                type="button"
                onClick={addColor}
                className="btn-add-color"
              >
                + Add color
              </button>
            </div>
          </div>

          <div className="form-group">
            <label>Highlights (Optional)</label>
            <p className="field-hint">
              Bullet points shown on the product page. Leave the label blank for
              a plain line such as &quot;1 Year Warranty&quot;.
            </p>
            <div className="highlights-list">
              {highlights.map((highlight, index) => (
                <div key={index} className="highlight-row">
                  <input
                    type="text"
                    value={highlight.label}
                    onChange={(e) =>
                      updateHighlight(index, 'label', e.target.value)
                    }
                    placeholder="Label (e.g., Movement)"
                    className="highlight-label-input"
                    maxLength={100}
                    aria-label={`Highlight ${index + 1} label`}
                  />
                  <input
                    type="text"
                    value={highlight.value}
                    onChange={(e) =>
                      updateHighlight(index, 'value', e.target.value)
                    }
                    placeholder="Value (e.g., Japanese Quartz)"
                    className="highlight-value-input"
                    maxLength={255}
                    aria-label={`Highlight ${index + 1} value`}
                  />
                  <button
                    type="button"
                    onClick={() => removeHighlight(index)}
                    className="btn-remove-color"
                    aria-label={`Remove highlight ${index + 1}`}
                  >
                    ×
                  </button>
                </div>
              ))}
              <button
                type="button"
                onClick={addHighlight}
                className="btn-add-color"
              >
                + Add highlight
              </button>
            </div>
          </div>

          <div className="form-group checkbox-group">
            <label className="checkbox-label">
              <input
                type="checkbox"
                name="is_best_seller"
                checked={formData.is_best_seller}
                onChange={handleChange}
              />
              <span>Mark as Best Seller</span>
            </label>
          </div>

          <div className="form-group checkbox-group">
            <label className="checkbox-label">
              <input
                type="checkbox"
                name="is_new_arrival"
                checked={formData.is_new_arrival}
                onChange={handleChange}
              />
              <span>Mark as New Arrival</span>
            </label>
          </div>

          {/* Primary Image Upload */}
          <div className="form-group">
            <label htmlFor="primaryImage">Primary Image</label>
            {primaryImagePreview ? (
              <div className="image-preview-container">
                <img
                  src={primaryImagePreview}
                  alt="Primary preview"
                  className="image-preview"
                />
                <button
                  type="button"
                  onClick={removePrimaryImage}
                  className="btn-remove-image"
                >
                  Remove
                </button>
              </div>
            ) : (
              <div className="file-upload-area">
                <input
                  type="file"
                  id="primaryImage"
                  accept="image/*"
                  onChange={handlePrimaryImageChange}
                  className="file-input"
                />
                <label htmlFor="primaryImage" className="file-upload-label">
                  <span className="upload-icon">📷</span>
                  <span>
                    {existingPrimaryImage
                      ? 'Click to replace primary image'
                      : 'Click to upload primary image'}
                  </span>
                  <span className="file-hint">JPG, PNG, WEBP (Max 5MB)</span>
                </label>
              </div>
            )}
          </div>

          {/* Additional Images Upload */}
          <div className="form-group">
            <label htmlFor="additionalImages">Additional Images (Optional)</label>
            {(additionalImagesPreview.length > 0 || existingImages.length > 0) && (
              <div className="images-preview-grid">
                {existingImages.map((img, index) => {
                  return (
                    <div key={img.id} className="image-preview-item">
                      <img
                        src={getImageUrl(img.image_url)}
                        alt={img.alt_text || `Existing ${index + 1}`}
                        className="image-preview-small"
                      />
                      <button
                        type="button"
                        onClick={() => removeAdditionalImage(index, true)}
                        className="btn-remove-image-small"
                      >
                        ×
                      </button>
                    </div>
                  );
                })}
                {additionalImagesPreview
                  .slice(existingImages.length)
                  .map((preview, index) => (
                    <div key={`new-${index}`} className="image-preview-item">
                      <img
                        src={preview}
                        alt={`New ${index + 1}`}
                        className="image-preview-small"
                      />
                      <button
                        type="button"
                        onClick={() =>
                          removeAdditionalImage(index + existingImages.length, false)
                        }
                        className="btn-remove-image-small"
                      >
                        ×
                      </button>
                    </div>
                  ))}
              </div>
            )}
            <div className="file-upload-area">
              <input
                type="file"
                id="additionalImages"
                accept="image/*"
                multiple
                onChange={handleAdditionalImagesChange}
                className="file-input"
              />
              <label htmlFor="additionalImages" className="file-upload-label">
                <span className="upload-icon">🖼️</span>
                <span>Click to add more images</span>
                <span className="file-hint">You can select multiple images (Max 10, 5MB each)</span>
              </label>
            </div>
          </div>

          {/* Error message at bottom */}
          {error && (
            <div className="error-message-bottom">
              <span className="error-icon">⚠️</span>
              <span className="error-text">{error}</span>
            </div>
          )}

          <div className="form-actions">
            <button
              type="button"
              onClick={() => navigate('/products')}
              className="btn-secondary"
              disabled={saving}
            >
              Cancel
            </button>
            <button type="submit" className="btn-primary" disabled={saving}>
              {saving ? 'Saving...' : 'Update Product'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default EditProduct;
